import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../../components/SelectField/SelectField';
import TextField from '../../components/TextField/TextField';
import { DeviceProfileDetailsActionKeyRowProps, useStyle } from './DeviceProfileDetailsActionKeyRow.utils';
import DeviceProfileDetailsActionKeyIconButton from './DeviceProfileDetailsActionKeyIconButton';
import { DropDownDictionaryItem } from '../CallHistory/ActiveCalls/ActiveCalls.utils';

const DeviceProfileDetailsActionKeyRow: React.FC<DeviceProfileDetailsActionKeyRowProps> = ({
    item,
    changeFunctionCallBack,
    changeValueCallBack,
    changeLabelCallBack,
    changeLineCallBack,
    setActiveButton,
    onChange,
    errors,
    readonly,
    activeButtonIdentifier,
}) => {
    const classes = useStyle();
    const {t} = useTranslation();

    const isAttributeConfigured = useCallback((attribute: 'value' | 'label' | 'line') => {
        if(!item?.selectedAction?.length) {
            return false;
        }
        const config = item.actionsConfiguration?.find(e => e.type === item?.selectedAction);
        if(!config) {
            return false;
        }
        const valueConfig = config?.action_attributes?.find(e => e.attribute === attribute);
        return !!valueConfig;
    }, [item]);
    
    const isAttributeMandatory = useCallback((attribute: 'value' | 'label' | 'line') => {
        if(!item?.selectedAction?.length) {
            return false;
        }
        const config = item.actionsConfiguration?.find(e => e.type === item?.selectedAction);
        if(!config) {
            return false;
        }
        const valueConfig = config?.action_attributes?.find(e => e.attribute === attribute);
        return valueConfig?.mandatory === 1;
    }, [item]);

    const valueConfigured = useMemo(() => {
        return isAttributeConfigured('value');
    }, [isAttributeConfigured]);

    const isValueMandatory = useMemo(() => {
        return isAttributeMandatory('value');
    }, [isAttributeMandatory]);
    
    const labelConfigured = useMemo(() => {
        return isAttributeConfigured('label');
    }, [isAttributeConfigured]);

    const isLabelMandatory = useMemo(() => {
        return isAttributeMandatory('label');
    }, [isAttributeMandatory]);
    
    const lineConfigured = useMemo(() => {
        return isAttributeConfigured('line');
    }, [isAttributeConfigured]);

    const isLineMandatory = useMemo(() => {
        return isAttributeMandatory('line');
    }, [isAttributeMandatory]);

    const linesLine = useMemo(() => {
        const retArray: DropDownDictionaryItem<number>[] = [];
        for(let i = 0; i < item.maxLines; i++) {
            retArray.push({
                name: i === 0 ? 'Auto' : i + '',
                value: i
            });
        }
        return retArray;
    }, [item]);

    return (<div className={classes.root} id={'device-profile-row-' + item.itemIdentifierInternal}>
        <DeviceProfileDetailsActionKeyIconButton
            size='medium'
            item={item}
            className={classes.iconButton}
            onClick={() => {
                setActiveButton(item);
            }}
            activeButtonIdentifier={activeButtonIdentifier}
        />

        <SelectField
            label={t('screens:deviceProfiles.function')}
            items={item?.actions ?? ['']}
            value={item?.selectedAction}
            dataQa="function-input"
            onChange={(e, value: string) => {
                changeFunctionCallBack?.(item, value);
                onChange(e);
            }}
            getOptionLabel={(v) => { 
                if(!v?.length) return '';
                return t('enums:UAProfileAction.' + v + '');
            }}
            getOptionSelected={(v) =>
                v === item.selectedAction
            }
            disableClearable
            classes={{
                container: classes.formFunction
            }}
            onFocus={() => {
                setActiveButton?.(item);
            }}
            readOnly={readonly}
        />
        
        <TextField
            label={t('screens:deviceProfiles.value')}
            value={item.value}
            className={classes.fieldValue}
            onChange={(e) => {
                changeValueCallBack?.(item, e.target.value);
                onChange(e);
            }}
            onFocus={() => {
                setActiveButton?.(item);
            }}
            helperText={errors?.value}
            disabled={readonly || !valueConfigured}
            required={!readonly && isValueMandatory}
            maxLength={255}
        />
        
        <SelectField
            label={t('screens:deviceProfiles.line')}
            items={linesLine}
            value={linesLine.find(e => e.value === item.line)}
            dataQa="line-input"
            onChange={(e, value: DropDownDictionaryItem<number>) => {
                changeLineCallBack?.(item, value.value ?? 0);
                onChange(e);
            }}
            getOptionLabel={(v: DropDownDictionaryItem<number>) => { 
                return v.name;
            }}
            getOptionSelected={(v) =>
                v === '1'
            }
            disableClearable
            classes={{
                container: classes.fieldLine
            }}
            onFocus={() => {
                setActiveButton?.(item);
            }}
            disabled={readonly || !lineConfigured}
            required={!readonly && isLineMandatory}
        />

        <TextField
            label={t('screens:deviceProfiles.label')}
            value={item.label}
            className={classes.fieldLabel}
            onChange={(e) => {
                changeLabelCallBack?.(item, e.target.value);
                onChange(e);
            }}
            onFocus={() => {
                setActiveButton?.(item);
            }}
            disabled={readonly || !labelConfigured}
            required={!readonly && isLabelMandatory}
            maxLength={255}
        />
    </div>);
};
    
export default DeviceProfileDetailsActionKeyRow;